import { useState } from 'react'
import type { ISensemakerOverviewProfile } from '@brushfire/types'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Link from '@mui/material/Link'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Typography from '@mui/material/Typography'
import { RiMoreFill } from 'react-icons/ri'
import {
  SENSEMAKER_OVERVIEW_PROFILE_AVATAR_GAP_PX,
  SENSEMAKER_OVERVIEW_PROFILE_AVATAR_SIZE_PX,
  SENSEMAKER_OVERVIEW_PROFILE_BADGE_HEIGHT_PX,
  SENSEMAKER_OVERVIEW_PROFILE_BAR_TO_TEAM_GAP_PX,
  SENSEMAKER_OVERVIEW_PROFILE_CARD_BG,
  SENSEMAKER_OVERVIEW_PROFILE_CARD_BORDER,
  SENSEMAKER_OVERVIEW_PROFILE_CARD_PADDING_BOTTOM_PX,
  SENSEMAKER_OVERVIEW_PROFILE_CARD_PADDING_TOP_PX,
  SENSEMAKER_OVERVIEW_PROFILE_CARD_PADDING_X_PX,
  SENSEMAKER_OVERVIEW_PROFILE_CARD_RADIUS_PX,
  SENSEMAKER_OVERVIEW_PROFILE_HEADER_TO_TOKEN_GAP_PX,
  SENSEMAKER_OVERVIEW_PROFILE_NAME_TYPE_GAP_PX,
  SENSEMAKER_OVERVIEW_PROFILE_STATUS_TO_PACKAGE_GAP_PX,
  SENSEMAKER_OVERVIEW_PROFILE_TITLE_SUBTITLE_GAP_PX,
  SENSEMAKER_OVERVIEW_PROFILE_TOKEN_BAR_HEIGHT_PX,
  SENSEMAKER_OVERVIEW_PROFILE_TOKEN_LABEL_TO_BAR_GAP_PX,
} from '../../lib/sensemaker-overview-layout'
import {
  formatOverviewDateTime,
  formatPackageLabel,
  SENSEMAKER_TYPE_TAG_LABEL,
  SENSEMAKER_TYPE_TAG_STYLES,
  tenantInitials,
  tokenBarColor,
} from '../../lib/sensemaker-overview-format'
import type { SensemakerTokenMock } from '../../lib/sensemaker-overview-token.mock'
import { SENSEMAKER_STATUS_COLORS, SENSEMAKER_STATUS_LABEL } from '../../lib/sensemaker-status'
import {
  sensemakerCardDeactivateMenuItemSx,
  sensemakerCardMenuPaperSx,
  sensemakerCardReactivateMenuItemSx,
} from '../../lib/sensemaker-card-menu'

interface Props {
  profile: ISensemakerOverviewProfile
  tokenMock: SensemakerTokenMock
  onTeamMembersClick: () => void
  onDeactivate?: () => void
  onReactivate?: () => void
}

export function SensemakerOverviewProfileHeader({
  profile,
  tokenMock,
  onTeamMembersClick,
  onDeactivate,
  onReactivate,
}: Readonly<Props>) {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)
  const typeStyles = SENSEMAKER_TYPE_TAG_STYLES[profile.typeTag] ?? SENSEMAKER_TYPE_TAG_STYLES.agency
  const statusColors = SENSEMAKER_STATUS_COLORS[profile.status]
  const typeLabel = SENSEMAKER_TYPE_TAG_LABEL[profile.typeTag] ?? profile.typeTag
  const showDeactivate = profile.status === 'active' && Boolean(onDeactivate)
  const showReactivate = profile.status === 'deactivated' && Boolean(onReactivate)
  const showStatusMenu = showDeactivate || showReactivate

  return (
    <Box
      data-testid="sensemaker-overview-profile-header"
      sx={{
        border: SENSEMAKER_OVERVIEW_PROFILE_CARD_BORDER,
        borderRadius: `${SENSEMAKER_OVERVIEW_PROFILE_CARD_RADIUS_PX}px`,
        px: `${SENSEMAKER_OVERVIEW_PROFILE_CARD_PADDING_X_PX}px`,
        pt: `${SENSEMAKER_OVERVIEW_PROFILE_CARD_PADDING_TOP_PX}px`,
        pb: `${SENSEMAKER_OVERVIEW_PROFILE_CARD_PADDING_BOTTOM_PX}px`,
        bgcolor: SENSEMAKER_OVERVIEW_PROFILE_CARD_BG,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: `${SENSEMAKER_OVERVIEW_PROFILE_AVATAR_GAP_PX}px` }}>
        <Box
          sx={{
            width: SENSEMAKER_OVERVIEW_PROFILE_AVATAR_SIZE_PX,
            height: SENSEMAKER_OVERVIEW_PROFILE_AVATAR_SIZE_PX,
            borderRadius: '50%',
            bgcolor: '#008cd0',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 600,
            fontSize: '16px',
            lineHeight: '22.5px',
            flexShrink: 0,
          }}
        >
          {tenantInitials(profile.tenantName)}
        </Box>

        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: `${SENSEMAKER_OVERVIEW_PROFILE_TITLE_SUBTITLE_GAP_PX}px`,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: `${SENSEMAKER_OVERVIEW_PROFILE_NAME_TYPE_GAP_PX}px`,
            }}
          >
            <Typography
              sx={{
                fontSize: '28px',
                fontWeight: 700,
                lineHeight: '42px',
                color: '#0f172a',
              }}
            >
              {profile.tenantName}
            </Typography>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: SENSEMAKER_OVERVIEW_PROFILE_BADGE_HEIGHT_PX,
                px: '10px',
                borderRadius: '100px',
                bgcolor: typeStyles.bg,
                border: `1px solid ${typeStyles.border}`,
                color: typeStyles.color,
                fontSize: '12px',
                fontWeight: 500,
                lineHeight: '16px',
                whiteSpace: 'nowrap',
              }}
            >
              {typeLabel}
            </Box>
          </Box>
          <Typography
            sx={{
              fontSize: '13px',
              fontWeight: 400,
              lineHeight: '19.5px',
              color: '#6b7a90',
            }}
          >
            Account created: {formatOverviewDateTime(profile.accountCreatedAt)}
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: `${SENSEMAKER_OVERVIEW_PROFILE_STATUS_TO_PACKAGE_GAP_PX}px`,
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: SENSEMAKER_OVERVIEW_PROFILE_BADGE_HEIGHT_PX,
                px: '11px',
                borderRadius: '999px',
                bgcolor: statusColors.bg,
                border: `1px solid ${statusColors.border}`,
                color: statusColors.color,
                fontSize: '12px',
                fontWeight: 500,
                lineHeight: '16px',
                whiteSpace: 'nowrap',
              }}
            >
              {SENSEMAKER_STATUS_LABEL[profile.status]}
            </Box>
            {showStatusMenu ? (
              <IconButton
                size="small"
                aria-label="Manage tenant status"
                data-testid="sensemaker-overview-status-menu"
                onClick={(e) => setMenuAnchor(e.currentTarget)}
                sx={{ p: '4px', color: '#0f172a' }}
              >
                <RiMoreFill size={16} />
              </IconButton>
            ) : null}
          </Box>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: SENSEMAKER_OVERVIEW_PROFILE_BADGE_HEIGHT_PX,
              px: '10px',
              borderRadius: '26px',
              bgcolor: '#e7fbff',
              border: '0.5px solid #008cd0',
              color: '#3b82f6',
              fontSize: '12px',
              fontWeight: 500,
              lineHeight: '16px',
              whiteSpace: 'nowrap',
              maxWidth: '100%',
            }}
          >
            {formatPackageLabel(profile.packageLabel)}
          </Box>
        </Box>
      </Box>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: sensemakerCardMenuPaperSx } }}
      >
        {showDeactivate ? (
          <MenuItem
            data-testid="sensemaker-overview-deactivate"
            onClick={() => {
              setMenuAnchor(null)
              onDeactivate?.()
            }}
            sx={sensemakerCardDeactivateMenuItemSx}
          >
            Deactivate Tenant
          </MenuItem>
        ) : null}
        {showReactivate ? (
          <MenuItem
            data-testid="sensemaker-overview-reactivate"
            onClick={() => {
              setMenuAnchor(null)
              onReactivate?.()
            }}
            sx={sensemakerCardReactivateMenuItemSx}
          >
            Reactivate Tenant
          </MenuItem>
        ) : null}
      </Menu>

      <Box sx={{ mt: `${SENSEMAKER_OVERVIEW_PROFILE_HEADER_TO_TOKEN_GAP_PX}px` }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 400,
              lineHeight: '16px',
              color: '#0f172a',
            }}
          >
            Campaign Token Balance
          </Typography>
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 500,
              lineHeight: '16px',
              color: '#0f172a',
              whiteSpace: 'nowrap',
            }}
            data-testid="sensemaker-token-balance-value"
          >
            {tokenMock.balancePercent == null ? 'N/A' : `${tokenMock.balancePercent}%`}
          </Typography>
        </Box>

        <Box
          sx={{
            mt: `${SENSEMAKER_OVERVIEW_PROFILE_TOKEN_LABEL_TO_BAR_GAP_PX}px`,
            height: SENSEMAKER_OVERVIEW_PROFILE_TOKEN_BAR_HEIGHT_PX,
            borderRadius: '9999px',
            bgcolor: '#e2e8f0',
            overflow: 'hidden',
          }}
          data-testid="sensemaker-token-balance-bar"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={tokenMock.balancePercent ?? 0}
          aria-valuetext={
            tokenMock.balancePercent == null ? 'No data' : `${tokenMock.balancePercent}%`
          }
        >
          {tokenMock.balancePercent != null ? (
            <Box
              sx={{
                width: `${tokenMock.balancePercent}%`,
                height: '100%',
                borderRadius: '9999px',
                bgcolor: tokenBarColor(tokenMock.balancePercent),
              }}
            />
          ) : null}
        </Box>

        {tokenMock.nearExhaustion && (
          <Typography
            sx={{ fontSize: '12px', color: '#ef4444', mt: '6px' }}
            data-testid="sensemaker-token-warning"
          >
            Near exhaustion — consider reaching out.
          </Typography>
        )}

        <Link
          component="button"
          type="button"
          onClick={onTeamMembersClick}
          underline="none"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            mt: `${SENSEMAKER_OVERVIEW_PROFILE_BAR_TO_TEAM_GAP_PX}px`,
            p: '10px',
            ml: '-10px',
            fontSize: '14px',
            fontWeight: 500,
            lineHeight: '16px',
            color: '#1976d2',
            cursor: 'pointer',
          }}
          data-testid="sensemaker-overview-team-link"
        >
          <PersonOutlineIcon sx={{ fontSize: 16, color: '#1976d2' }} />
          Team Members ({profile.teamMemberCount})
        </Link>
      </Box>
    </Box>
  )
}
